/**
 * VikunjaConnectModal — Vikunja setup (instance URL + API token).
 *
 * Vikunja is a SELF-HOSTED kanban board: there is no vikunja.com to OAuth
 * against and no admin token-minting API (tokens are per-user rows the user
 * creates in their own board), so a connection is exactly two values:
 *
 *   - Instance URL — the board's base address, e.g. https://board.example.com.
 *                    A board served under a path prefix keeps the prefix
 *                    (https://box.example.com/sidecars/vikunja) — that prefix
 *                    is part of the API base, and the backend's normalizer
 *                    preserves it while trimming a trailing /api/v1.
 *   - API token    — minted in Vikunja → Settings → API Tokens.
 *
 * The backend (POST /vikunja/connect → handlers/vikunja.js) probes
 * `GET <instance>/api/v1/projects` with the token BEFORE persisting, so a bad
 * URL or an under-scoped token is rejected here rather than at run time, and
 * the success payload names the boards it found. Structured errors (400 for a
 * rejected token / non-public URL, 502 for an unreachable instance) surface
 * inline on err.message, with the backend's optional `hint` on a second line.
 *
 * ⚠️ CLOUD-SPECIFIC, and the reason the URL field carries a warning: on cloud
 * the instance URL is a user-supplied host that OUR Lambda then fetches, so it
 * goes through the DNS-resolving SSRF guard (utils/ssrf-guard assertPublicUrl)
 * — private, loopback and metadata addresses are refused. A board on a LAN or
 * behind a VPN is reachable from a self-host box but NOT from cloud; that is a
 * deliberate boundary, not a bug, and the copy says so up front instead of
 * letting the user discover it as an opaque 400.
 *
 * Reuses the shared .lark-*-modal CSS (same as SentryConnectModal) so styling
 * matches the other paste-token connect modals exactly.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectVikunja } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

// The permission groups an agent's board work actually needs. Listed because a
// token minted with the default (empty) selection passes nothing — Vikunja's
// token UI is a checkbox tree, and the first-run failure is otherwise a bare
// "Vikunja rejected the API token".
// GENERATED from packages/workflow-templates/vikunja/token-permissions.mjs
// (vikunjaScopeLine) — the tripwire there fails when this drifts.
const REQUIRED_SCOPES = 'projects: read_all, read_one, tasks_by_index, create, update, views_buckets, views_buckets_tasks, views_buckets_put, views_buckets_post · projects_views: read_all, read_one · projects_views_tasks: read_all · tasks: read_all, read_one, create, update, position · tasks_comments: read_all, read_one, create, update · tasks_assignees: create · other: user, users · tasks_attachments: read_all, read_one, create · tasks_labels: create, delete, read_all, update_bulk · labels: read_all, read_one, create, update';

export default function VikunjaConnectModal({ isOpen, onClose, onConnected }) {
  const [instanceUrl, setInstanceUrl] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectVikunja();

  useEffect(() => {
    if (isOpen) {
      setInstanceUrl('');
      setToken('');
      setError('');
      setHint('');
      setConnecting(false);
    }
  }, [isOpen]);

  const canSubmit = Boolean(instanceUrl.trim() && token.trim());

  const handleSubmit = () => {
    if (!canSubmit) {
      setError('Instance URL and API token are both required.');
      return;
    }
    setConnecting(true);
    setError('');
    setHint('');
    mutation.mutate(
      { instanceUrl: instanceUrl.trim(), token: token.trim() },
      {
        onSuccess: (data) => {
          setConnecting(false);
          // Never keep the pasted credential in component state after it has
          // been persisted (it is write-once from here — /vikunja/status only
          // ever returns a 4-char mask).
          setToken('');
          onConnected?.(data);
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Vikunja. Check the instance URL and API token.');
          // The backend attaches an optional `hint` on an unreachable-instance
          // 502; ApiClient preserves the whole payload on err.body.
          setHint(err?.body?.hint || '');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Vikunja"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Vikunja</h2>
        <p className="lark-modal-description">
          Vikunja has no OAuth — mint an API token in{' '}
          <strong>Vikunja → Settings → API Tokens</strong> and paste it with your
          board&apos;s address. Give the token these permission groups:{' '}
          <code>{REQUIRED_SCOPES}</code>
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="vikunja-instance-url">Instance URL</label>
          <input
            id="vikunja-instance-url"
            type="text"
            value={instanceUrl}
            placeholder="https://board.example.com"
            onChange={(e) => { setInstanceUrl(e.target.value || ''); setError(''); setHint(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="lark-modal-description" style={{ marginTop: 6 }}>
            Must be reachable from the public internet — Zibby Cloud cannot dial a
            board on a private network or <code>localhost</code>. If the board is
            served under a path prefix, include it
            (e.g. <code>https://box.example.com/sidecars/vikunja</code>).
          </p>
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="vikunja-token">API token</label>
          <input
            id="vikunja-token"
            type="password"
            value={token}
            placeholder="tk_..."
            onChange={(e) => { setToken(e.target.value || ''); setError(''); setHint(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && (
          <p className="lark-modal-error" role="alert">
            {error}
            {hint ? <><br />{hint}</> : null}
          </p>
        )}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !canSubmit}
            aria-label="Submit Vikunja credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Vikunja connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
